import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatISO } from 'date-fns'

import { debounce } from '@mui/material'
import { ApplicationState } from '@/state/root.models'
import { DateFilterEnum, PersonalizationEngineReportTypeEnum } from '@/enums'

import { PersonalizationEngineFiltersType } from '@/state/personalization-engine/models'
import { dateFilters } from '@/constants/date'
import { usePersonalizationEngineActions } from './'

export const usePersonalizationEngineFilters = () => {
  const { filters } = useSelector(
    ({ personalizationEngine }: ApplicationState) => personalizationEngine,
  )

  const [date, setDate] = useState<DateFilterEnum>(DateFilterEnum.LAST_7_DAYS)

  const {
    getGameCategories,
    getGameProviders,
    getFavoriteGames,
    setPersonalizationEngineFilters,
  } = usePersonalizationEngineActions()

  // eslint-disable-next-line
  const filterDelay = useCallback(
    debounce((newFilters: PersonalizationEngineFiltersType) => {
      getGameCategories({
        ...newFilters,
        reportType: PersonalizationEngineReportTypeEnum.CATEGORY,
      })
      getGameProviders({
        ...newFilters,
        reportType: PersonalizationEngineReportTypeEnum.PROVIDER,
      })
      getFavoriteGames({
        ...newFilters,
        reportType: PersonalizationEngineReportTypeEnum.GAME,
      })
    }, 500),
    [],
  )

  const handleChangeSearch = (
    field: string,
    value: string,
    debounce?: boolean,
  ) => {
    const newFilters = {
      ...filters,
      [field]: value,
      page: 1,
    }

    setPersonalizationEngineFilters(newFilters)

    if (field !== 'transactionTypePayment') {
      if (debounce) {
        filterDelay(newFilters)
      } else {
        getGameCategories({
          ...newFilters,
          reportType: PersonalizationEngineReportTypeEnum.CATEGORY,
        })
        getGameProviders({
          ...newFilters,
          reportType: PersonalizationEngineReportTypeEnum.PROVIDER,
        })
        getFavoriteGames({
          ...newFilters,
          reportType: PersonalizationEngineReportTypeEnum.GAME,
        })
      }
    }
  }

  const handleChangeDate = (newDate: DateFilterEnum) => {
    setDate(newDate)
    if (newDate !== DateFilterEnum.DATE_RANGE) {
      const newFilters = {
        ...filters,
        startDate: dateFilters[newDate].dateStart,
        endDate: dateFilters[newDate].dateEnd,
        page: 1,
      }
      setPersonalizationEngineFilters(newFilters)
      filterDelay(newFilters)
    }
  }

  const onChangeStartDate = (newDate: Date) => {
    handleChangeSearch('startDate', formatISO(newDate) as string)
  }

  const onChangeEndDate = (newDate: Date) => {
    handleChangeSearch('endDate', formatISO(newDate) as string)
  }

  return {
    filters,
    date,
    handleChangeDate,
    handleChangeSearch,
    onChangeEndDate,
    onChangeStartDate,
  }
}
